import React, { FC, ReactNode, useEffect, useMemo, useState, useCallback } from 'react';
import { Spin } from 'antd';
import { useRouter } from 'next/router';

import { useTranslation } from 'next-i18next';
import Switch from '../Switch';
import Pagination from './Pagination';
import styles from './PageList.module.scss';

interface DataItemType {
  [key: string]: any;
}

interface PageType {
  pageSize: number;
  pageNo: number;
  totalCount?: number;
}

export interface OnLoadDataResType {
  data: DataItemType[];
  page: PageType;
  status?: any;
}

export interface Props {
  defaultPageSize: number;
  prePageText?: string;
  nextPageText?: string;
  renderItem: (item: DataItemType) => ReactNode;
  onLoadData: (page: PageType) => Promise<OnLoadDataResType>;
}

const PageList: FC<Props> = (props) => {
  const { t } = useTranslation('common');
  const { defaultPageSize, prePageText, nextPageText, onLoadData: onLoadDataProps, renderItem } = props;

  const playgroundDetailsConfig: {
    all: string;
    doing: string;
    end: string;
  } = t('playgroundDetailsConfig', { returnObjects: true });

  const [data, setData] = useState([] as DataItemType);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState({
    pageNo: 1,
    pageSize: defaultPageSize,
  });
  const [selectedIndex, setSelectedIndex] = useState('1');
  const { locale } = useRouter();

  // 切换页码
  const onPageSizeChange = (page: number, pageSize: number) => {
    setPages({ pageNo: page, pageSize });
  };
  // 请求函数
  const onLoadData = useCallback(
    async ({ pageNo, pageSize, status }: any) => {
      const params = { pageNo, pageSize, status };
      try {
        setLoading(true);
        const { data: resData, page: resPage } = await onLoadDataProps(params);
        const { totalCount } = resPage as { totalCount: number };
        setData(resData);
        setTotal(totalCount);
        // 切换完页面滚动到顶部 todo==增加滚动到容器视窗内
        document.documentElement.scrollTop = 0;
      } finally {
        setLoading(false);
      }
    },
    [onLoadDataProps],
  );

  const status = useMemo(() => {
    if (selectedIndex === '1') {
      return 'EXECUTED';
    }
    if (selectedIndex === '2') {
      return 'RESERVE';
    }
    return '';
  }, [selectedIndex]);

  const handleSelectTag = ({ value }: { value: string }) => {
    setSelectedIndex(value);
    onPageSizeChange(1, defaultPageSize);
  };

  // 所有请求的params准备
  const params = useMemo(() => {
    return {
      ...pages,
      status,
      lanType: locale,
    };
  }, [pages, locale, status]);

  const switchOptions = [
    { text: playgroundDetailsConfig.all, value: '0' },
    { text: playgroundDetailsConfig.end, value: '1' },
    { text: playgroundDetailsConfig.doing, value: '2' },
  ];

  useEffect(() => {
    // 初始化的时候执行一次
    onLoadData(params);
  }, [params, onLoadData]);

  const listItemNode =
    Array.isArray(data) &&
    data.map((item, idx) => {
      const RenderItemNode = renderItem(item);
      return (
        <div className={styles['list-item']} key={idx}>
          {RenderItemNode}
        </div>
      );
    });

  return (
    <div className={styles['page-list-container']}>
      <Spin spinning={loading}>
        <div className={styles['list-wrap']}>
          <Switch
            className={styles['switch-container']}
            value={selectedIndex}
            onChange={handleSelectTag}
            options={switchOptions}
          />
          <div className={styles['list-container']}>{listItemNode}</div>
        </div>
        {!!total && (
          <Pagination
            key={status}
            preText={prePageText}
            nextText={nextPageText}
            pageSize={defaultPageSize}
            total={total}
            onChange={onPageSizeChange}
          />
        )}
      </Spin>
    </div>
  );
};

export default PageList;
